<template>
  <div ref="mathContainer">
    <div>
      <p id="question">{{ getQuestionDescription(exercise.description) }}</p>
      <img
        v-if="exercise.graphUrl"
        :src="exercise.graphUrl"
        alt=""
        class="mt-3 mb-5 m-auto"
        style="max-width: 800px"
      />
    </div>

    <div class="flex justify-center items-center text-center mt-6" v-if="showHint">
      <div class="w-full max-w-lg">
        <button @click="toggleCollapseHint" class="hint-button-2 px-4 py-2 rounded-md mb-4">
          ¿Necesitas ayuda?
        </button>
        <div v-if="isCollapsedHint" class="hint-container p-4">
          <!-- Contenido colapsable -->
          <p class="text-justify ">{{ exercise.hint }}</p>
        </div>
      </div>
    </div>

    <div class="space-y-5">
      <template v-if="isOpenEnded">
        <p>Escribe tu respuesta (máx. 200 caracteres)</p>
        <div class="flex flex-col gap-2">
          <textarea
            v-model="openEndedText"
            class="w-full border rounded-md p-3 min-h-[80px] max-h-[120px] resize-y"
            maxlength="200"
            placeholder="Tu respuesta..."
            :disabled="responseSent"
            @paste.prevent
            @contextmenu.prevent
          />
          <span class="text-sm text-gray-500">{{ (openEndedText || '').length }} / 200</span>
        </div>
        <div v-if="responseSent && openEndedCorrectResult !== null" class="mt-3 p-3 rounded-md" :class="openEndedCorrectResult ? 'bg-green-50 border border-green-500' : 'bg-red-50 border border-red-500'">
          {{ openEndedCorrectResult ? 'Correcto' : 'Incorrecto' }}
        </div>
      </template>
      <template v-else>
        <p>Selecciona una respuesta</p>
        <div class="flex flex-col gap-5">
          <MobRadio
            v-if="exercise.answers"
            :options="exercise.answers"
            inputName="exercise-response"
            v-model="selectedResponse"
            :disabled="responseSent"
          />
        </div>
      </template>

      <button
        v-if="!responseSent && !noCheck"
        class="mob-btn mob-cta-gold mt-5"
        :disabled="comprobarDisabled"
        @click="checkResponse(true)"
      >
        Comprobar
      </button>

      <button
        v-if="(responseSent || noCheck) && !lastExercise"
        class="mob-btn mob-cta-gold-outline mt-5"
        :disabled="exercise.response"
        @click="nextExcercise()"
      >
        Siguiente
      </button>      
    </div>

    <div class="bg-white shadow-md rounded-md mt-4" v-if="viewSolution && responseSent && (exercise.solution || isOpenEnded)">
      <!-- Same context-based visibility for open-ended: show solution block when viewSolution allows -->
      <div class="border-b border-gray-200 px-3 py-2 flex justify-between items-center header-solution">
        <h6 class="text-xs  font-semibold text-gray-600">VER EXPLICACIÓN</h6>
        <button @click="toggleCollapse" class="focus:outline-none">
          <svg :class="{'transform rotate-180': !isCollapsed}" class="h-6 w-6 fill-current text-gray-600" viewBox="0 0 24 24">
            <path d="M12 15l-8-8h16z"></path>
          </svg>
        </button>
      </div>

      <div v-if="!isCollapsed" class="px-4 py-3">
        <p v-if="exercise.solution">{{ exercise.solution }}</p>
        <p v-else-if="isOpenEnded && referenceSolutionText" class="text-gray-700">{{ referenceSolutionText }}</p>
        <p v-else-if="isOpenEnded" class="text-gray-500">Respuesta abierta calificada por significado.</p>
      </div>
    </div>

  </div>

</template>

<script>
import { MobRadio } from "@/views/V2/components";
import { checkLessonExerciseResponse } from "@/services/lessons.service.js";
import { setQuestionnaireFinished } from "@/services/attemps.service";
import { getCurrentQuestionAnswer, registerAnswer, registerOpenEndedAnswer } from "@/services/questions.service.js";

export default {
  name: "ExerciseItem",
  components: {
    MobRadio,
  },
  props: {
    noCheck: Boolean,
    origin: String,
    originId: String,
    lastExercise: Boolean,
    allowCorrection: Boolean,
    viewSolution: Boolean,
    showHint: Boolean,
    isReview: Boolean,
    exercise: {
      type: Object,
      required: true,
      options: Array,
    },
  },
  data() {
    return {
      correct: null,
      selectedResponse: null,
      openEndedText: "",
      openEndedCorrectResult: null,
      responseSent: false,
      registered: false,
      currentAnswer: null,
      isCollapsed: false,
      isCollapsedHint: false,
      questionStartTime: null,
      /** Reference solution for open-ended when backend sends it (Practice/review). */
      referenceSolutionText: null,
    };
  },
  watch: {

    exercise: {

      immediate: false,
      async handler(newVal) {
        if (!newVal || !newVal.id) return;
        this.isCollapsedHint = false;
        this.correct = null;
        this.selectedResponse = null;
        this.openEndedText = "";
        this.openEndedCorrectResult = null;
        this.responseSent = false;
        this.registered = false;
        this.currentAnswer = null;
        this.referenceSolutionText = null;

        this.questionStartTime = new Date().toISOString();

        if (this.moduleId != null) {
          try {
            this.currentAnswer = await getCurrentQuestionAnswer(this.exercise.id, this.origin, this.originId, this.moduleId);
          } catch (e) {
            console.error("ExerciseItem: getCurrentQuestionAnswer failed", e);
            this.currentAnswer = null;
          }
        } else {
          this.currentAnswer = null;
        }
        if (newVal.id) {
          if (this.currentAnswer && this.currentAnswer.length > 0) {
            this.registered = true;
            const latest = this.pickLatestAttemptDto(this.currentAnswer);
            if (latest.answer) {
              this.selectedResponse = latest.answer.id + "";
            } else if (latest.openEndedResponse != null) {
              this.openEndedText = latest.openEndedResponse;
              this.openEndedCorrectResult = latest.openEndedCorrect;
              this.responseSent = true;
              if (latest.referenceAnswer != null && latest.referenceAnswer !== "") {
                this.referenceSolutionText = latest.referenceAnswer;
              }
            }
          }
        }

        this.$nextTick(function () {
          const latest = this.pickLatestAttemptDto(this.currentAnswer);
          if (latest && latest.answer && !this.isOpenEnded) {
            this.selectedResponse = latest.answer.id + "";
            if (!this.allowCorrection) {
              this.checkResponse();
            }
          }
        });

        // if ('MathJax' in window) {
        //   this.$nextTick(function() {
        //     window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
        //   });
        // }

      },
    },
    selectedResponse: {
      immediate: true,
      async handler(newVal) {
        if (this.allowCorrection && !this.isOpenEnded) {
          await this.registerAnswerFn();
        }
      }
    }
  },
  computed: {
    course() {
      return this.$store.state.course.current;
    },
    /** Course id from store, or from route when store not yet set (e.g. simulacrum after refresh). */
    moduleId() {
      const c = this.course;
      const routeId = this.$route?.params?.id;
      if (c && c.id != null) return c.id;
      if (routeId != null && routeId !== "") return Number(routeId) || routeId;
      return null;
    },
    isOpenEnded() {
      if (!this.exercise) return false;
      if (this.exercise.questionType === "OPEN_ENDED") return true;
      // Fallback: no answer options (or all empty) => treat as open-ended so student sees text input
      const answers = this.exercise.answers || [];
      if (answers.length === 0) return true;
      const hasRealOption = answers.some((a) => a.description && String(a.description).trim());
      return !hasRealOption;
    },
    openEndedTextTrimmed() {
      return (this.openEndedText || "").trim();
    },
    /** Comprobar is enabled when: open-ended and has text (length 1–200) or MCQ and option selected */
    comprobarDisabled() {
      if (this.isOpenEnded) {
        const text = (this.openEndedText || "").trim();
        return text.length === 0 || text.length > 200;
      }
      return !this.selectedResponse;
    },
  },
  methods: {
    /** Backend may return several rows; use the latest by finishedAt then id (aligns with AttemptsService). */
    pickLatestAttemptDto(list) {
      if (!list || list.length === 0) return null;
      if (list.length === 1) return list[0];
      const sorted = [...list].sort((a, b) => {
        const fa = a.finishedAt || "";
        const fb = b.finishedAt || "";
        if (fa !== fb) return fa.localeCompare(fb);
        return (Number(a.id) || 0) - (Number(b.id) || 0);
      });
      return sorted[sorted.length - 1];
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      if ('MathJax' in window) {
        this.$nextTick(function() {
          window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
        });
      }
    },
    toggleCollapseHint() {
      this.isCollapsedHint = !this.isCollapsedHint;
      if ('MathJax' in window) {
        this.$nextTick(function() {
          window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
        });
      }
    },
    getQuestionDescription: function(question){
      const elements = document.getElementById("question");
      if(elements){
        elements.innerText = question;
      } else {
        return question;
      }
    },
    /** Heurística: texto largo suele hacer wrap y puede solaparse con "Seleccionada" */
    isOptionTextLong(option) {
      const text = option && (option.description || "");
      const len = typeof text === "string" ? text.length : 0;
      return len > 30;
    },
    async checkResponse(forceRegister = false) {
      if (this.allowCorrection || forceRegister) {
        await this.registerAnswerFn();
      }

      // Open-ended: backend already graded via ChatGPT in registerAttempt; fetch result and show correct/incorrect
      if (this.isOpenEnded) {
        try {
          const list = await getCurrentQuestionAnswer(this.exercise.id, this.origin, this.originId, this.moduleId);
          if (list && list.length > 0) {
            const first = list[0];
            this.openEndedCorrectResult = first.openEndedCorrect !== undefined ? first.openEndedCorrect : false;
            if (first.referenceAnswer != null && first.referenceAnswer !== "") {
              this.referenceSolutionText = first.referenceAnswer;
            }
          } else {
            this.openEndedCorrectResult = false;
          }
        } catch (e) {
          console.error(e);
          this.openEndedCorrectResult = false;
        }
        this.responseSent = true;
        if ("MathJax" in window) {
          this.$nextTick(() => window.MathJax?.Hub?.Queue?.(["Typeset", window.MathJax.Hub]));
        }
        return;
      }

      const response = await checkLessonExerciseResponse(
        this.exercise.id, this.origin, this.originId, this.moduleId
      );
      let selectedOption = this.exercise.answers.find(
        (option) => option.id == this.selectedResponse
      );
      const correctOption = this.exercise.answers.find(
        (answer) => Array.isArray(response) && response[0] && answer.id == response[0].id
      );

      if (!correctOption) {
        this.responseSent = true;
        return;
      }

      if (this.isReview && !selectedOption) {
        correctOption.extraClases =
          "border border-green-500 bg-green-50 p-2 -m-2 rounded-md exercise-item-response exercise-item-response--correct";
      } else {
        if (selectedOption && selectedOption.id === correctOption.id) {
          const longTextClass = this.isOptionTextLong(selectedOption) ? " exercise-item-response--long-text" : "";
          selectedOption.extraClases =
            "border border-green-500 bg-green-50 p-2 -m-2 rounded-md exercise-item-response exercise-item-response--selected exercise-item-response--correct" + longTextClass;
        } else {
          if (selectedOption) {
            const longTextClass = this.isOptionTextLong(selectedOption) ? " exercise-item-response--long-text" : "";
            selectedOption.extraClases =
              "border border-red-500 bg-red-50 p-2 -m-2 rounded-md exercise-item-response exercise-item-response--selected exercise-item-response--incorrect" + longTextClass;
          }

          if (this.selectedResponse) {
            correctOption.extraClases =
              "border border-green-500 bg-green-50 p-2 -m-2 rounded-md exercise-item-response exercise-item-response--correct";
          }
        }
      }
      
      if (this.selectedResponse) {
        this.responseSent = true;
      }      

      if ('MathJax' in window) {
        this.$nextTick(function() {
          window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
        });
      }
    },
    async finishQuestionnaire() {
      try {
        await this.registerAnswerFn();
      } catch (e) {
        console.error("ExerciseItem.finishQuestionnaire: registerAnswerFn", e);
      }
      if (this.moduleId == null) {
        window.alert("No se pudo identificar el curso. Vuelve a entrar al examen desde la fase olímpica.");
        return;
      }
      try {
        await setQuestionnaireFinished(this.origin, this.originId, this.moduleId);
        this.$emit("goToResults");
      } catch (e) {
        console.error("ExerciseItem.finishQuestionnaire: setQuestionnaireFinished", e);
        window.alert("No se pudo finalizar el examen. Pulsa Finalizar de nuevo o contacta soporte si persiste.");
      }
    },
    async registerAnswerFn() {
      if (this.moduleId == null) {
        console.warn("ExerciseItem: moduleId missing, cannot register attempt (course or route id required)");
        return;
      }
      const startedAt = this.questionStartTime || new Date().toISOString();
      if (this.isOpenEnded) {
        const text = this.openEndedTextTrimmed;
        if (text && text.length <= 200) {
          await registerOpenEndedAnswer(this.exercise.id, text, this.origin, this.originId, this.moduleId, startedAt);
          this.registered = true;
        }
      } else if (this.selectedResponse) {
        await registerAnswer(this.exercise.id, this.selectedResponse, this.origin, this.originId, this.moduleId, startedAt);
        this.registered = true;
      }
    },
    nextExcercise() {
      if (this.allowCorrection){
        this.registerAnswerFn()
      }
      this.$emit("nextQuestion", this.exercise.id);
    },
  },
};
</script>

<style>
.exercise-item-response {
  position: relative;
}

.exercise-item-response--selected::before {
  content: "Seleccionada";
  position: absolute;
  right: 2.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: #f1f5f9;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  line-height: 1.2;
}

/* Si el texto de la opción es largo, "Seleccionada" va debajo y alineada a la derecha para evitar solapamiento (en móvil y escritorio) */
.exercise-item-response--selected.exercise-item-response--long-text {
  padding-bottom: 1.5rem;
}
.exercise-item-response--selected.exercise-item-response--long-text::before {
  top: auto;
  bottom: 0;
  right: 2.5rem;
}

.exercise-item-response::after {
  position: absolute;
  right: 0.5rem;
}

/* Reservar espacio a la derecha para el icono y evitar que el texto se solape */
.exercise-item-response--correct,
.exercise-item-response--incorrect {
  padding-right: 2.75rem;
}

/* Asegurar que el contenedor de texto deje espacio interno para el icono */
.exercise-item-response .radio + span {
  display: block;
  padding-right: 2.75rem;
}

.exercise-item-response--correct::after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' viewBox='0 0 24 24'%3E%3Cpath fill='%234CAF50' d='M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2Zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8Z'/%3E%3Cpath fill='%234CAF50' d='m10 13.587-2.3-2.295-1.412 1.416 3.713 3.705 6.706-6.706-1.414-1.414L10 13.587Z'/%3E%3C/svg%3E%0A");
}

.exercise-item-response--incorrect::after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' viewBox='0 0 24 24'%3E%3Cpath fill='%23D52F19' d='M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828 1.414 1.414Z'/%3E%3Cpath fill='%23D52F19' d='M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10Zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8Z'/%3E%3C/svg%3E%0A");
}

.hint-container {
  background-color: #eff6ff;
  border-radius: 7px;
  border: 1px solid #2563eb;
}
.hint-button-2 {
  background-color: #eff6ff;
  color: #2563eb;
}

.header-solution {
  border: 1px solid #454f4912;
  background-color: #eff6ff; border-top-left-radius: 10px; border-top-right-radius: 10px;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>
